import type { McpEndpoint } from "../hooks/use-mcp-endpoints";

/**
 * Sprint 16f — tool list at scale. When a workspace synthesises more tools than a
 * host can ingest in one `tools/list`, attaching the umbrella `/mcp` truncates the
 * tool list and wrapped tools silently fail to register. This pure planner decides
 * whether to recommend scoped endpoints (`/mcp/lite` + per-wrapper `/mcp/w/<slug>`)
 * over the umbrella, given a tool count and the caller's credentialled wrappers.
 *
 * `60` sits comfortably above Cursor's ~40-tool cap and below Cowork's observed
 * ~150K-char (~80–100 tool) ingest wall. It's the single tuning knob — adjust here
 * and both the Connect dropdown and the Settings → Endpoints banner follow.
 */
export const TOOL_COUNT_THRESHOLD = 60;

export interface RecommendedEndpoint {
  path: string;
  url: string;
  label: string;
  toolCount: number;
  /** Short copy explaining why this endpoint is recommended. */
  reason: string;
  kind: "lite" | "wrapper";
}

export interface AttachmentPlan {
  /** "scoped" once the workspace exceeds the host-ingest threshold. */
  strategy: "umbrella" | "scoped";
  totalToolCount: number;
  threshold: number;
  exceedsThreshold: boolean;
  /** Ordered scoped endpoints to attach: `/mcp/lite` first, then credentialled wrappers. */
  recommended: RecommendedEndpoint[];
  /** The umbrella `/mcp` endpoint — demoted to "advanced" when scoped. */
  umbrella: McpEndpoint | null;
}

export interface RecommendAttachmentInput {
  /** The live `list_mcp_endpoints` catalogue (each carries an absolute `url`). */
  endpoints: McpEndpoint[];
  /** Total workspace tool count — i.e. the umbrella `/mcp` endpoint's tool_count. */
  totalToolCount: number;
  /** Slugs of wrappers the current user has a credential set for. */
  credentialledWrappers: string[];
  threshold?: number;
}

/**
 * Build an ordered attachment plan. Below threshold → keep the umbrella (no UI
 * change for typical workspaces). Above → recommend `/mcp/lite` plus one
 * `/mcp/w/<slug>` per credentialled wrapper, and demote the umbrella to advanced.
 */
export function getRecommendedAttachment(input: RecommendAttachmentInput): AttachmentPlan {
  const threshold = input.threshold ?? TOOL_COUNT_THRESHOLD;
  const exceedsThreshold = input.totalToolCount > threshold;
  const umbrella = input.endpoints.find((e) => e.path === "/mcp") ?? null;

  const recommended: RecommendedEndpoint[] = [];

  const lite = input.endpoints.find((e) => e.path === "/mcp/lite");
  if (lite) {
    recommended.push({
      path: lite.path,
      url: lite.url,
      label: lite.label,
      toolCount: lite.tool_count,
      reason: "Curated subset — native primitives, small enough for any host.",
      kind: "lite",
    });
  }

  const credentialled = new Set(input.credentialledWrappers);
  for (const e of input.endpoints) {
    if (!e.path.startsWith("/mcp/w/")) continue;
    const slug = e.path.slice("/mcp/w/".length);
    if (!credentialled.has(slug)) continue;
    recommended.push({
      path: e.path,
      url: e.url,
      label: e.label,
      toolCount: e.tool_count,
      reason: "Only this wrapped API's tools — isolated, always loads.",
      kind: "wrapper",
    });
  }

  return {
    strategy: exceedsThreshold ? "scoped" : "umbrella",
    totalToolCount: input.totalToolCount,
    threshold,
    exceedsThreshold,
    recommended,
    umbrella,
  };
}
